import { useEffect, useState } from 'react';
import type { ListhubList, TenantListhubSettings } from '@ww/shared';
import { api } from '../api';
import { ErrorBox, Loading, Toggle } from './ui';
import { I, Icon } from '../lib/icons';
import { errorMessage } from '../lib/status';

/**
 * ListHub: email weather alerts and road closures to one of the tenant's ListHub lists.
 *
 * The tenant brings a ListHub API key; WeatherWatch uses it to show their lists and to
 * register its own mailbox as a sender on the chosen one. The alerts themselves go out
 * as email to the list address, because ListHub has no send API.
 */
export default function ListhubCard({ admin }: { admin: boolean }) {
  const [lh, setLh] = useState<TenantListhubSettings | null>(null);
  const [lists, setLists] = useState<ListhubList[] | null>(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) return;
    api.listhub().then(setLh, (e) => setError(errorMessage(e)));
  }, [admin]);

  // Lists are fetched live from ListHub once a key is in.
  useEffect(() => {
    if (!lh?.configured) {
      setLists(null);
      return;
    }
    api.listhubLists().then(
      (r) => setLists(r.lists),
      (e) => setError(errorMessage(e)),
    );
  }, [lh?.configured, lh?.keyHint]);

  const run = async (fn: () => Promise<TenantListhubSettings | void>, message: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await fn();
      if (next) setLh(next);
      setNotice(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!admin) {
    return (
      <section className="panel" style={{ padding: 16, maxWidth: 760 }}>
        <span className="muted">Only a county administrator can see or change the ListHub connection.</span>
      </section>
    );
  }
  if (!lh && !error) return <Loading />;

  const ready = !!lh?.listAddress && lh.senderReady;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {notice && (
        <div className="ok-box" role="status">
          {notice}
        </div>
      )}
      <ErrorBox error={error} />

      <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon d={I.msg} size={18} style={{ color: '#9fb0c8' }} />
          <strong style={{ fontSize: 15.5 }}>Send through ListHub</strong>
          <span
            style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: lh?.configured ? '#6fdc98' : '#ffc36b' }}
          >
            {lh?.configured ? `Connected · ${lh.keyHint}` : 'Not connected'}
          </span>
        </div>
        <span className="muted" style={{ fontSize: 13.5 }}>
          If your county has a ListHub account, WeatherWatch can email weather alerts and road closures to one of
          your ListHub lists, and ListHub passes them on to everyone on it. This runs alongside SMS and follows the
          same alert rules.
        </span>

        <label className="field">
          <span className="lbl">ListHub API key</span>
          <input
            className="fld mono"
            type="password"
            autoComplete="off"
            placeholder={lh?.configured ? 'Connected — paste a new key to replace it' : 'lh_xxxxxxxx.xxxxxxxx…'}
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
          />
        </label>
        <span className="muted" style={{ fontSize: 13 }}>
          In ListHub, open Settings → API keys and create a key with the <strong>tenant admin</strong> role (it is needed
          to let WeatherWatch post to your list). Paste it here; it is stored for this county only and never shown
          again.
        </span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary hov"
            disabled={busy || !keyDraft.trim()}
            onClick={() =>
              void run(async () => {
                const next = await api.setListhubKey(keyDraft.trim());
                setKeyDraft('');
                return next;
              }, 'ListHub connected. Now choose the list to send to.')
            }
          >
            {busy ? 'Checking…' : lh?.configured ? 'Replace key' : 'Connect'}
          </button>
          {lh?.configured && (
            <button
              type="button"
              className="btn btn-sm btn-danger hov"
              style={{ marginLeft: 'auto' }}
              disabled={busy}
              onClick={() => void run(() => api.disconnectListhub(), 'ListHub disconnected. Nothing more will be sent there.')}
            >
              Disconnect
            </button>
          )}
        </div>
      </section>

      {lh?.configured && (
        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="field">
            <span className="lbl">List to send to</span>
            <select
              className="fld"
              disabled={busy || !lists}
              value={lh.listId ?? ''}
              onChange={(e) =>
                e.target.value &&
                void run(() => api.updateListhub({ listId: e.target.value }), 'List chosen.')
              }
            >
              <option value="">{lists ? 'Choose a list…' : 'Loading lists…'}</option>
              {(lists ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.primaryAddress} · {l.memberCount} members
                </option>
              ))}
            </select>
          </label>

          {lh.listId && (
            <div className={lh.senderReady ? 'ok-box' : 'warn-box'} style={{ fontSize: 13.5 }}>
              {lh.senderReady
                ? `Ready: WeatherWatch sends from ${lh.senderAddress} to ${lh.listAddress}.`
                : `Not ready yet: ${lh.senderDetail ?? 'the WeatherWatch sender is not set up on this list.'}`}
              {!lh.senderReady && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="btn-link hov"
                    disabled={busy}
                    onClick={() => void run(() => api.updateListhub({ listId: lh.listId! }), 'Checked the list again.')}
                  >
                    Try again
                  </button>
                </>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle
              on={lh.sendAlerts}
              label="Send weather alerts to ListHub"
              disabled={busy}
              onChange={(next) =>
                void run(() => api.updateListhub({ sendAlerts: next }), next ? 'Weather alerts will go to ListHub.' : 'Weather alerts will no longer go to ListHub.')
              }
            />
            <span>Weather alerts</span>
            <span className="muted" style={{ fontSize: 13 }}>
              — the alerts that would text staff, under the same rules
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle
              on={lh.sendClosures}
              label="Send road closures to ListHub"
              disabled={busy}
              onChange={(next) =>
                void run(() => api.updateListhub({ sendClosures: next }), next ? 'Road closures will go to ListHub.' : 'Road closures will no longer go to ListHub.')
              }
            />
            <span>Road closures</span>
            <span className="muted" style={{ fontSize: 13 }}>
              — closures and reopenings posted to notify someone
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary hov"
              disabled={busy || !ready}
              onClick={() =>
                void run(async () => {
                  const r = await api.testListhub();
                  setNotice(r.detail);
                  return api.listhub();
                }, 'Test sent to the list. Members should receive it within a minute or two.')
              }
            >
              Send a test to the list
            </button>
            {lh.lastSendAt && (
              <span className="muted" style={{ fontSize: 13 }}>
                Last send {lh.lastSendResult ?? 'unknown'} at {new Date(lh.lastSendAt).toLocaleString()}
                {lh.lastSendDetail ? ` · ${lh.lastSendDetail}` : ''}
              </span>
            )}
          </div>
          {lh.updatedAt && (
            <span className="muted" style={{ fontSize: 13 }}>
              Changed {new Date(lh.updatedAt).toLocaleString()}
              {lh.updatedBy ? ` by ${lh.updatedBy}` : ''}
            </span>
          )}
        </section>
      )}
    </div>
  );
}
